import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { Alert } from '@mui/material';

import { newTraceId, newTraceparent } from '../../utils/traceparent';

export const CLOSING_FAST_POLL_MS = 3000;
export const CLOSING_SLOW_POLL_MS = 10000;
export const CLOSING_FAST_WINDOW_MS = 30000;
export const CLOSING_LIMIT_MS = 20 * 60 * 1000;
const MAX_FAILURES = 3;

const enabled = () => process.env.REACT_APP_BATCH_SIGN_POLLING === 'true';

// Fechamento do lote (US-008/US-009). Com a flag desligada fica sempre em 'open' e não chama nada.
// phase: checking | open | closing | finished | canceled | timeout. loadState(batchKey, traceparent) rejeita com toApiError.
export function useBatchClosing(batchKey, loadState) {
  const on = enabled();
  const [phase, setPhase] = useState(on ? 'checking' : 'open');
  const [counts, setCounts] = useState(null);
  const [failing, setFailing] = useState(false);
  const traceId = useRef(null);
  const startedAt = useRef(0);
  const hintMs = useRef(0);

  const readState = (data) => {
    setCounts(data.finished_count == null ? null : { done: data.finished_count, total: data.total_count ?? null });
    hintMs.current = (data.state_poll_after_s || 0) * 1000;
  };

  const startClosing = (data) => {
    startedAt.current = Date.now();
    readState(data);
    setPhase('closing');
  };

  useEffect(() => {
    if (!on) return undefined;
    let cancelled = false;
    loadState(batchKey).then(
      (data) => {
        if (cancelled) return;
        if (data.batch_state === 'closing') startClosing(data);
        else setPhase(['finished', 'canceled'].includes(data.batch_state) ? data.batch_state : 'open');
      },
      () => !cancelled && setPhase('open')
    );
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- só na montagem
  }, []);

  useEffect(() => {
    if (phase !== 'closing') return undefined;
    let stopped = false;
    let timer;
    let failures = 0;

    const schedule = () => {
      const elapsed = Date.now() - startedAt.current;
      const base = elapsed < CLOSING_FAST_WINDOW_MS ? CLOSING_FAST_POLL_MS : CLOSING_SLOW_POLL_MS;
      const delay = Math.max(base, hintMs.current);
      hintMs.current = 0;
      timer = setTimeout(tick, delay);
    };

    const tick = () => {
      if (Date.now() - startedAt.current >= CLOSING_LIMIT_MS) {
        setPhase('timeout');
        return;
      }
      traceId.current = traceId.current || newTraceId();
      loadState(batchKey, newTraceparent(traceId.current)).then(
        (data) => {
          if (stopped) return;
          failures = 0;
          setFailing(false);
          if (data.batch_state === 'finished' || data.batch_state === 'canceled') {
            setPhase(data.batch_state);
            return;
          }
          readState(data);
          schedule();
        },
        (apiError) => {
          if (stopped) return;
          hintMs.current = (apiError.retryAfter || 0) * 1000;
          failures += 1;
          if (failures >= MAX_FAILURES) setFailing(true);
          schedule();
        }
      );
    };

    schedule();
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reinicia só ao entrar em 'closing'
  }, [phase]);

  // Novo traceparent para a ação de assinar (undefined com a flag desligada)
  const beginTrace = () => {
    if (!on) return undefined;
    traceId.current = newTraceId();
    return newTraceparent(traceId.current);
  };

  // Resposta da confirmação: true = permanece na tela (batch_state closing)
  const onSigned = (data) => {
    if (!on || data?.batch_state !== 'closing') return false;
    startClosing(data);
    return true;
  };

  return { phase, counts, failing, beginTrace, onSigned };
}

const TEXTS = {
  finished: ['success', 'Lote finalizado. Todas as assinaturas foram concluídas.'],
  canceled: ['warning', 'Lote cancelado. As assinaturas deste lote não serão concluídas.'],
  timeout: ['info', 'O processamento continua. Você pode fechar esta página.'],
};

export default function BatchClosingStatus({ phase, counts, failing }) {
  if (phase === 'checking') return null;
  const [severity, text] = TEXTS[phase] || ['info', 'Assinatura registrada, finalizando o lote.'];
  return (
    <Alert severity={severity} role="status" aria-live="polite" sx={{ width: '100%', maxWidth: 440 }}>
      {text}
      {phase === 'closing' && counts && (
        <div>
          {counts.total == null
            ? `Envelopes finalizados: aproximadamente ${counts.done}.`
            : `Envelopes finalizados: aproximadamente ${counts.done} de ${counts.total}.`}
        </div>
      )}
      {phase === 'closing' && failing && (
        <div>Não foi possível atualizar o andamento agora. Continuamos tentando.</div>
      )}
    </Alert>
  );
}

BatchClosingStatus.propTypes = {
  phase: PropTypes.string.isRequired,
  counts: PropTypes.shape({ done: PropTypes.number, total: PropTypes.number }),
  failing: PropTypes.bool,
};
